
  import { defineComponent, PropType } from 'vue'

  type Period = {
    start: string
    end: string
  }

  /** 비-isProduct 분기에서 쓸 수 있는 빠른 기간 키 */
  export type QuickPeriodKey = 'today' | 'week' | 'month' | 'quarter' | 'half' | 'year'

  const PERIOD_LABELS: Record<QuickPeriodKey, string> = {
    today: '오늘',
    week: '1주일',
    month: '1개월',
    quarter: '3개월',
    half: '6개월',
    year: '1년',
  }

  const DEFAULT_PERIODS: QuickPeriodKey[] = ['today', 'week', 'month', 'quarter']

  export default defineComponent({
    name: 'QuickPeriodComponent',

    props: {
      modelValue: { type: [Object, String] as PropType<Period>, default: () => ({ start: '', end: '' }) },
      disabled: { type: Boolean, default: false },
      isProduct: { type: Boolean, default: false },
      isAllowToday: { type: Boolean, default: true },
      maxDate: { type: String, default: '' },
      // 비-isProduct 분기의 버튼 구성(순서대로). 생략하면 기존 4개 그대로. `isProduct` 분기는 이 prop 을 쓰지 않는다
      periods: { type: Array as PropType<QuickPeriodKey[]>, default: () => DEFAULT_PERIODS },
    },

    emits: ['update:modelValue'],

    data() {
      return {
        hide: this.disabled,
        date: this.modelValue,
        PERIOD_LABELS,
      }
    },

    computed: {
      max() {
        return this.maxDate ? this.$moment(this.maxDate).format('YYYY-MM-DD') : ''
      },

      visiblePeriods(): QuickPeriodKey[] {
        return this.periods.filter((period) => period in PERIOD_LABELS && (period !== 'today' || this.isAllowToday))
      },
    },

    watch: {
      disabled(val: boolean) {
        this.hide = val
      },
    },

    methods: {
      setTotal() {
        this.date = {
          start: '2016-07-01',
          end: this.$moment().format('YYYY-MM-DD'),
        }
        this.$emit('update:modelValue', this.date)
      },

      setToday() {
        this.date = {
          start: this.$moment(this.max || undefined).format('YYYY-MM-DD'),
          end: this.max ? this.max : this.$moment().format('YYYY-MM-DD'),
        }
        this.$emit('update:modelValue', this.date)
      },

      setWeek() {
        this.date = {
          start: this.$moment(this.max || undefined)
            .subtract(1, 'week')
            .format('YYYY-MM-DD'),
          end: this.max ? this.max : this.$moment().format('YYYY-MM-DD'),
        }
        this.$emit('update:modelValue', this.date)
      },

      setMonth() {
        this.date = {
          start: this.$moment(this.max || undefined)
            .subtract(1, 'month')
            .format('YYYY-MM-DD'),
          end: this.max ? this.max : this.$moment().format('YYYY-MM-DD'),
        }
        this.$emit('update:modelValue', this.date)
      },

      setQuarter() {
        this.date = {
          start: this.$moment(this.max || undefined)
            .subtract(3, 'month')
            .format('YYYY-MM-DD'),
          end: this.max ? this.max : this.$moment().format('YYYY-MM-DD'),
        }
        this.$emit('update:modelValue', this.date)
      },

      setYear() {
        this.date = {
          start: this.$moment().subtract(1, 'year').format('YYYY-MM-DD'),
          end: this.$moment().format('YYYY-MM-DD'),
        }
        this.$emit('update:modelValue', this.date)
      },

      /** 6개월 — 다른 버튼과 같이 `max` 기준 (비-isProduct 분기 전용) */
      setHalf() {
        this.setFromMax(6, 'month')
      },

      /** 1년(비-isProduct 분기 전용) — `max` 기준. isProduct 의 `setYear` 는 그대로 둔다 */
      setOneYear() {
        this.setFromMax(1, 'year')
      },

      setFromMax(amount: number, unit: 'month' | 'year') {
        this.date = {
          start: this.$moment(this.max || undefined)
            .subtract(amount, unit)
            .format('YYYY-MM-DD'),
          end: this.max ? this.max : this.$moment().format('YYYY-MM-DD'),
        }
        this.$emit('update:modelValue', this.date)
      },

      setPeriod(period: QuickPeriodKey) {
        switch (period) {
          case 'today':
            return this.setToday()
          case 'week':
            return this.setWeek()
          case 'month':
            return this.setMonth()
          case 'quarter':
            return this.setQuarter()
          case 'half':
            return this.setHalf()
          case 'year':
            return this.setOneYear()
        }
      },
    },
  })
